import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BudgetCard } from './budget-card';
import { BudgetTable } from './budget-table';
import { BudgetProgressBar } from './budget-progress-bar';
import { formatCurrency, formatPercent } from '@/lib/budget-utils';
import type { CostBudget } from '@/types/budget.types';

interface CostBudgetViewProps {
  data: CostBudget;
}

export function CostBudgetView({ data }: CostBudgetViewProps) {
  console.log('[CostBudgetView] Received data:', data);
  
  if (!data || !data.totals || !data.categories || !data.phases) {
    console.log('[CostBudgetView] Missing required data:', {
      hasData: !!data,
      hasTotals: !!data?.totals,
      hasCategories: !!data?.categories,
      hasPhases: !!data?.phases,
    });
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No cost budget data available</p>
      </div>
    );
  }

  const { totals: summary, categories, phases } = data;
  console.log('[CostBudgetView] Summary:', summary);
  console.log('[CostBudgetView] Categories count:', categories.length);
  console.log('[CostBudgetView] Phases count:', phases.length);

  // Calculate segments for stacked progress bar
  const totalBudget = summary.budget;
  const progressSegments = [
    {
      value: (summary.actual / totalBudget) * 100,
      color: 'bg-blue-500',
      label: 'Actual',
    },
    {
      value: (summary.committed / totalBudget) * 100,
      color: 'bg-amber-500',
      label: 'Committed',
    },
    {
      value: (summary.available / totalBudget) * 100,
      color: 'bg-gray-300 dark:bg-gray-700',
      label: 'Available',
    },
  ];

  // Prepare category table data
  const categoryRows = categories.map((category) => ({
    id: category.id,
    name: category.name,
    budget: formatCurrency(category.budget),
    actual: formatCurrency(category.actual),
    committed: category.committed > 0 ? formatCurrency(category.committed) : '-',
    available: formatCurrency(category.available),
    percent: formatPercent(category.progressPercent),
    children: category.subcategories?.map((sub) => ({
      id: sub.id,
      name: sub.name,
      budget: formatCurrency(sub.budget),
      actual: formatCurrency(sub.actual),
      committed: sub.committed > 0 ? formatCurrency(sub.committed) : '-',
      available: formatCurrency(sub.available),
      percent: formatPercent(sub.progressPercent),
    })),
  }));

  // Prepare phase table data
  const phaseRows = phases.map((phase) => ({
    id: phase.id,
    phase: phase.phaseName,
    budget: formatCurrency(phase.budget),
    actual: formatCurrency(phase.actual),
    variance: formatCurrency(phase.variance),
    status: phase.status,
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BudgetCard
          title="Budget"
          value={formatCurrency(summary.budget)}
          variant="default"
        />
        <BudgetCard
          title="Actual"
          value={formatCurrency(summary.actual)}
          subtitle={formatPercent(summary.actual / summary.budget)}
          variant="default"
        />
        <BudgetCard
          title="Committed"
          value={formatCurrency(summary.committed)}
          subtitle={formatPercent(summary.committed / summary.budget)}
          variant="default"
        />
        <BudgetCard
          title="Available"
          value={formatCurrency(summary.available)}
          subtitle={formatPercent(summary.available / summary.budget)}
          variant="default"
        />
      </div>

      {/* Progress Bar */}
      <Card>
        <CardContent className="pt-6">
          <BudgetProgressBar
            variant="stacked"
            segments={progressSegments}
            showLegend
          />
        </CardContent>
      </Card>

      {/* Breakdown by Category */}
      <Card>
        <CardHeader>
          <CardTitle>Breakdown by Category</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetTable
            variant="expandable"
            columns={[
              { key: 'name', label: 'Category', align: 'left' },
              { key: 'budget', label: 'Budget', align: 'right' },
              { key: 'actual', label: 'Actual', align: 'right' },
              { key: 'committed', label: 'Committed', align: 'right' },
              { key: 'available', label: 'Available', align: 'right' },
              { key: 'percent', label: '%', align: 'right' },
            ]}
            data={categoryRows}
            totalRow={{
              name: 'TOTAL',
              budget: formatCurrency(summary.budget),
              actual: formatCurrency(summary.actual),
              committed: formatCurrency(summary.committed),
              available: formatCurrency(summary.available),
              percent: formatPercent((summary.actual + summary.committed) / summary.budget),
            }}
          />
        </CardContent>
      </Card>

      {/* Breakdown by Phase */}
      <Card>
        <CardHeader>
          <CardTitle>Breakdown by Phase</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetTable
            variant="simple"
            columns={[
              { key: 'phase', label: 'Phase', align: 'left' },
              { key: 'budget', label: 'Budget', align: 'right' },
              { key: 'actual', label: 'Actual', align: 'right' },
              { key: 'variance', label: 'Variance', align: 'right' },
              { key: 'status', label: 'Status', align: 'left' },
            ]}
            data={phaseRows}
          />
        </CardContent>
      </Card>
    </div>
  );
}
